import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { authFetch } from "../../App";
import { toast } from "sonner";
import {
  Search, ChevronRight, ChevronDown, Eye, EyeOff, Loader2,
  Sparkles, Download, FileDown, Zap,
} from "lucide-react";
import { Button } from "../../components/ui/button";

const API = "/api";
const fmt = (n) => n != null ? n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "\u2014";

export function BlockCalculation({ block, onReload }) {
  const [search, setSearch] = useState("");
  const [filterMode, setFilterMode] = useState("all");
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [pendingUpdates, setPendingUpdates] = useState({});
  const [summary, setSummary] = useState(null);
  const saveTimer = useRef(null);

  const loadSummary = useCallback(async () => {
    const r = await authFetch(`${API}/gaeb/blocks/${block.id}/summary`);
    if (r.ok) setSummary(await r.json());
  }, [block.id]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const savePending = useCallback(async (updates) => {
    const items = Object.entries(updates).map(([id, u]) => ({ id, ...u }));
    if (!items.length) return;
    await authFetch(`${API}/gaeb/blocks/${block.id}/positions`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(items),
    });
    setPendingUpdates({});
    loadSummary();
  }, [block.id, loadSummary]);

  const updateEP = (posId, value) => {
    const ep = parseFloat(value.replace(",", "."));
    if (isNaN(ep)) return;
    const allPos = [...(block.positions || []), ...(block.lv_structure || []).filter(i => i.type === "position")];
    const pos = allPos.find((p) => p.id === posId);
    if (pos) {
      pos.ep = ep;
      pos.gb = Math.round(ep * (pos.menge || 0) * 100) / 100;
    }
    const next = { ...pendingUpdates, [posId]: { ep } };
    setPendingUpdates(next);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => savePending(next), 1500);
  };

  const toggleVisibility = async (posId) => {
    const allPos = [...(block.positions || []), ...(block.lv_structure || []).filter(i => i.type === "position")];
    const pos = allPos.find((p) => p.id === posId);
    if (pos) pos.visible = !pos.visible;
    await authFetch(`${API}/gaeb/blocks/${block.id}/positions`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify([{ id: posId, visible: pos?.visible }]),
    });
    loadSummary();
  };

  const bulkAction = async (action, groupKey, visible) => {
    await authFetch(`${API}/gaeb/blocks/${block.id}/bulk-visibility`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, group_key: groupKey, visible }),
    });
    onReload();
  };

  const handleAutoPrice = async () => {
    const res = await authFetch(`${API}/gaeb/blocks/${block.id}/auto-price`, { method: "POST" });
    if (res.ok) {
      const result = await res.json();
      toast.success(result.message);
      onReload();
    } else {
      toast.error("Auto-Bepreisung fehlgeschlagen");
    }
  };

  const handleExport = async (type) => {
    const url = type === "p84"
      ? `${API}/gaeb/blocks/${block.id}/export?bidder=TGAone%20GmbH`
      : `${API}/gaeb/blocks/${block.id}/export-csv`;
    const res = await authFetch(url);
    if (res.ok) {
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = type === "p84" ? `Angebot_${block.block_name}.P84` : `Kalkulation_${block.block_name}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success(type === "p84" ? "P84 exportiert" : "CSV exportiert");
    }
  };

  const toggleGroup = (oz) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(oz)) next.delete(oz); else next.add(oz);
      return next;
    });
  };

  const structure = block.lv_structure || [];
  const hasStructure = structure.length > 0;
  const positions = block.positions || [];

  const filteredStructure = useMemo(() => {
    if (!hasStructure) return [];
    let items = structure;
    const q = search.toLowerCase().trim();
    const matchIds = new Set();
    const parentOzs = new Set();
    for (const item of items) {
      if (item.type !== "position") continue;
      let show = true;
      if (filterMode === "visible") show = item.visible;
      if (filterMode === "relevant") show = item.auto_relevant;
      if (q && show) show = (item.text || "").toLowerCase().includes(q) || (item.kurztext || "").toLowerCase().includes(q) || (item.oz_display || "").includes(q);
      if (show) {
        matchIds.add(item.id);
        if (item.oz) {
          parentOzs.add(item.oz.substring(0, 2));
          if (item.oz.length >= 4) parentOzs.add(item.oz.substring(0, 4));
        }
      }
    }
    return items.filter(item => {
      if (item.type === "position") return matchIds.has(item.id);
      if (item.type === "gruppe") return parentOzs.has(item.oz);
      if (item.type === "hinweis") return !q;
      return false;
    });
  }, [structure, hasStructure, filterMode, search]);

  const legacyGroups = useMemo(() => {
    if (hasStructure) return [];
    const g = {};
    for (const pos of positions) {
      const key = `${pos.kg}.${pos.titel}`;
      if (!g[key]) g[key] = { kg: pos.kg, titel: pos.titel, positions: [] };
      g[key].positions.push(pos);
    }
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [positions, hasStructure]);

  const filteredLegacy = useMemo(() => {
    return legacyGroups.map(([key, grp]) => {
      let filtered = grp.positions;
      if (filterMode === "visible") filtered = filtered.filter((p) => p.visible);
      if (filterMode === "relevant") filtered = filtered.filter((p) => p.auto_relevant);
      if (search.trim()) {
        const q = search.toLowerCase();
        filtered = filtered.filter((p) => (p.text || "").toLowerCase().includes(q) || p.oz.includes(q));
      }
      return filtered.length ? [key, { ...grp, positions: filtered }] : null;
    }).filter(Boolean);
  }, [legacyGroups, filterMode, search]);

  const CalcTypeBadge = ({ type }) => {
    const styles = { gruppe: "bg-blue-100 text-blue-700", hinweis: "bg-purple-100 text-purple-700", position: "bg-gray-100 text-gray-600" };
    const labels = { gruppe: "Grp", hinweis: "Hinw", position: "Pos" };
    return <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${styles[type] || ""}`}>{labels[type]}</span>;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Button size="sm" onClick={handleAutoPrice} className="gap-1.5 text-[11px] h-8 bg-amber-500 hover:bg-amber-600" data-testid={`block-auto-price-${block.id}`}>
            <Zap className="w-3.5 h-3.5" /> Auto-EP
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleExport("p84")} className="gap-1.5 text-[11px] h-8" data-testid={`block-export-p84-${block.id}`}>
            <FileDown className="w-3.5 h-3.5" /> P84
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleExport("csv")} className="gap-1.5 text-[11px] h-8" data-testid={`block-export-csv-${block.id}`}>
            <Download className="w-3.5 h-3.5" /> CSV
          </Button>
        </div>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => bulkAction("show_relevant")} className="gap-1 text-[11px] h-8">
          <Sparkles className="w-3 h-3" /> Nur Relevante
        </Button>
        <Button size="sm" variant="outline" onClick={() => bulkAction("show_all")} className="gap-1 text-[11px] h-8">
          <Eye className="w-3 h-3" /> Alle
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {[
            { label: "Gesamt", value: summary.total_positions, sub: "Positionen" },
            { label: "Sichtbar", value: summary.visible_positions, sub: "Positionen" },
            { label: "Kalkuliert", value: summary.calculated_positions, sub: "mit EP" },
            { label: "Netto", value: `${fmt(summary.total_netto)} EUR`, sub: `zzgl. ${summary.ust_pct}% USt.` },
            { label: "Brutto", value: `${fmt(summary.total_brutto)} EUR`, sub: "Block-Summe", highlight: true },
          ].map(({ label, value, sub, highlight }) => (
            <div key={label} className={`rounded-lg border p-2.5 ${highlight ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"}`}>
              <p className={`text-[10px] uppercase tracking-wide mb-0.5 ${highlight ? "text-slate-400" : "text-gray-400"}`}>{label}</p>
              <p className={`text-sm font-bold ${highlight ? "text-white" : "text-gray-900"}`}>{value}</p>
              <p className={`text-[10px] ${highlight ? "text-slate-400" : "text-gray-400"}`}>{sub}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suchen..." className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300" data-testid={`block-search-${block.id}`} />
        </div>
        <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden">
          {[
            { key: "all", label: "Alle" },
            { key: "visible", label: "Sichtbar" },
            { key: "relevant", label: "Relevant" },
          ].map((f) => (
            <button key={f.key} onClick={() => setFilterMode(f.key)} className={`px-2.5 py-1.5 text-[11px] font-semibold border-r last:border-r-0 border-gray-200 transition-colors ${filterMode === f.key ? "bg-slate-800 text-white" : "text-gray-600 hover:bg-gray-50"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {hasStructure ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-[44px_80px_1fr_80px_42px_90px_90px_32px] gap-1 px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-400 font-semibold sticky top-0 z-10">
            <span>Typ</span><span>OZ</span><span>Beschreibung</span><span className="text-right">Menge</span><span>ME</span><span className="text-right">EP</span><span className="text-right">GP</span><span></span>
          </div>
          <div className="divide-y divide-gray-50">
            {(() => {
              let trackGroupOz = "";
              return filteredStructure.map((item) => {
                if (item.type === "gruppe") trackGroupOz = item.oz;
                const parentCollapsed = item.type !== "gruppe" && [...collapsedGroups].some(oz =>
                  (item.type === "position" && item.oz && item.oz.startsWith(oz)) ||
                  (item.type === "hinweis" && trackGroupOz && (trackGroupOz === oz || trackGroupOz.startsWith(oz)))
                );
                if (parentCollapsed) return null;
                if (item.type === "gruppe" && item.level > 1) {
                  const parentOz = item.oz.substring(0, (item.level - 1) * 2);
                  if (collapsedGroups.has(parentOz)) return null;
                }
                const indent = ((item.level || 0) - 1) * 16;

                if (item.type === "gruppe") {
                  const isCollapsed = collapsedGroups.has(item.oz);
                  return (
                    <div key={item.id} className={`grid grid-cols-[44px_80px_1fr_80px_42px_90px_90px_32px] gap-1 px-3 py-2 items-center cursor-pointer hover:bg-blue-50/60 ${item.level === 1 ? "bg-blue-50/70 border-l-[3px] border-l-blue-400" : "bg-blue-50/30 border-l-[3px] border-l-blue-200"}`} onClick={() => toggleGroup(item.oz)} data-testid={`calc-group-${item.oz}`}>
                      <CalcTypeBadge type="gruppe" />
                      <span className="text-[11px] font-mono font-bold text-blue-800">{item.oz_display}</span>
                      <div className="flex items-center gap-1.5 min-w-0" style={{ paddingLeft: indent }}>
                        {isCollapsed ? <ChevronRight className="w-3.5 h-3.5 text-blue-400 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-blue-400 shrink-0" />}
                        <span className="text-[12px] font-semibold text-blue-900 truncate">{item.kurztext}</span>
                      </div>
                      <span /><span /><span /><span /><span />
                    </div>
                  );
                }

                if (item.type === "hinweis") {
                  return (
                    <div key={item.id} className="grid grid-cols-[44px_80px_1fr_80px_42px_90px_90px_32px] gap-1 px-3 py-1.5 items-start bg-purple-50/30 border-l-[3px] border-l-purple-200">
                      <CalcTypeBadge type="hinweis" />
                      <span />
                      <p className="text-[11px] text-purple-700 truncate" style={{ paddingLeft: indent + 16 }}>{item.kurztext}</p>
                      <span /><span /><span /><span /><span />
                    </div>
                  );
                }

                return (
                  <div key={item.id} className={`grid grid-cols-[44px_80px_1fr_80px_42px_90px_90px_32px] gap-1 px-3 py-1.5 items-center text-[12px] transition-colors ${!item.visible ? "opacity-40 bg-gray-50" : item.auto_relevant ? "bg-amber-50/40 border-l-[3px] border-l-amber-300" : "border-l-[3px] border-l-transparent"}`} data-testid={`calc-pos-${item.oz_display || item.oz}`}>
                    <CalcTypeBadge type="position" />
                    <span className="font-mono text-[10px] text-gray-500">{item.oz_display}</span>
                    <span className="text-gray-700 truncate" style={{ paddingLeft: indent + 16 }} title={item.text}>{item.text?.substring(0, 60) || item.kurztext}</span>
                    <span className="text-right font-medium text-[11px]">{item.menge != null ? fmt(item.menge) : ""}</span>
                    <span className="text-gray-400 text-[10px]">{item.einheit}</span>
                    <input type="text" defaultValue={item.ep != null ? item.ep.toString().replace(".", ",") : ""} onBlur={(e) => e.target.value && updateEP(item.id, e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.target.blur()} placeholder="—" className="text-right text-[12px] font-medium border border-gray-200 rounded px-1.5 py-0.5 w-full focus:outline-none focus:ring-1 focus:ring-slate-400" disabled={!item.visible} data-testid={`calc-ep-${item.oz_display || item.oz}`} />
                    <span className="text-right font-semibold text-gray-800 text-[12px]">{item.gb != null ? fmt(item.gb) : "\u2014"}</span>
                    <button onClick={() => toggleVisibility(item.id)} className="p-0.5 hover:bg-gray-100 rounded">
                      {item.visible ? <Eye className="w-3.5 h-3.5 text-gray-400" /> : <EyeOff className="w-3.5 h-3.5 text-gray-300" />}
                    </button>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {filteredLegacy.map(([key, grp]) => {
            const grpVisible = grp.positions.filter((p) => p.visible).length;
            const grpTotal = grp.positions.length;
            const isOpen = !collapsedGroups.has(key);
            return (
              <div key={key} className="border-b border-gray-100 last:border-b-0">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50/80 cursor-pointer hover:bg-gray-100/80" onClick={() => toggleGroup(key)}>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  <span className="text-xs font-mono text-slate-500">{key}</span>
                  <span className="text-sm font-medium text-gray-700 flex-1">{block.groups?.[key]?.label || `Titel ${grp.titel}`}</span>
                  <span className="text-[10px] text-gray-400">{grpVisible}/{grpTotal}</span>
                  <button onClick={(e) => { e.stopPropagation(); bulkAction("toggle_group", key, grpVisible < grpTotal); }} className="p-1 hover:bg-gray-200 rounded">
                    {grpVisible < grpTotal ? <Eye className="w-3.5 h-3.5 text-gray-400" /> : <EyeOff className="w-3.5 h-3.5 text-gray-400" />}
                  </button>
                </div>
                {isOpen && (
                  <div>
                    <div className="grid grid-cols-[auto_1fr_80px_50px_100px_100px_36px] gap-2 px-4 py-1.5 text-[10px] uppercase tracking-wide text-gray-400 font-semibold border-b border-gray-100">
                      <span className="w-20">OZ</span><span>Beschreibung</span><span className="text-right">Menge</span><span>ME</span><span className="text-right">EP</span><span className="text-right">GP</span><span></span>
                    </div>
                    {grp.positions.map((pos) => (
                      <div key={pos.id} className={`grid grid-cols-[auto_1fr_80px_50px_100px_100px_36px] gap-2 px-4 py-2 items-center border-b border-gray-50 text-[13px] transition-colors ${!pos.visible ? "opacity-40 bg-gray-50" : pos.auto_relevant ? "bg-amber-50/40" : ""}`} data-testid={`gaeb-pos-${pos.oz}`}>
                        <span className="w-20 font-mono text-[11px] text-slate-400">{pos.oz_display || pos.oz}</span>
                        <span className="text-gray-700 truncate" title={pos.text}>{pos.text?.substring(0, 80) || pos.kurztext}</span>
                        <span className="text-right font-medium">{pos.menge != null ? fmt(pos.menge) : "\u2014"}</span>
                        <span className="text-gray-400 text-[11px]">{pos.einheit}</span>
                        <input type="text" defaultValue={pos.ep != null ? pos.ep.toString().replace(".", ",") : ""} onBlur={(e) => e.target.value && updateEP(pos.id, e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.target.blur()} placeholder="\u2014" className="text-right text-[13px] font-medium border border-gray-200 rounded px-2 py-1 w-full focus:outline-none focus:ring-1 focus:ring-slate-400" disabled={!pos.visible} data-testid={`gaeb-ep-${pos.oz}`} />
                        <span className="text-right font-semibold text-gray-800">{pos.gb != null ? fmt(pos.gb) : "\u2014"}</span>
                        <button onClick={() => toggleVisibility(pos.id)} className="p-1 hover:bg-gray-100 rounded">
                          {pos.visible ? <Eye className="w-3.5 h-3.5 text-gray-400" /> : <EyeOff className="w-3.5 h-3.5 text-gray-300" />}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
